import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import Course from "../models/course.model";
import Pagination, { CourseSpecParams } from "../models/pagination";
import * as firebase from "firebase/app";
import "firebase/firestore";

@Injectable({
  providedIn: "root",
})
export class CourseService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  userLogin: User;
  private dbPath = "/courses";
  coursesRef: AngularFirestoreCollection<Course>;

  constructor(
    private http: HttpClient,
    private db: AngularFirestore,
    private userStore: UserService
  ) {
    this.coursesRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  //Using firestore courses
  getFsPublishedCourses(tutorId: string, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("published", "==", true)
      .where("privacy", "==", false);

    if (tutorId != "") {
      ref = ref.where("createdBy", "==", tutorId);
    }

    ref = ref.orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  //Using data in local
  getPublishedCourses(specParams: CourseSpecParams) {
    let params = new HttpParams();
    if (specParams.pageSize) {
      params = params.append("pageSize", specParams.pageSize.toString());
    }
    if (specParams.pageNumber) {
      params = params.append("pageIndex", specParams.pageNumber.toString());
    }
    if (specParams.search) {
      params = params.append("search", specParams.search);
    }
    if (specParams.uid) {
      params = params.append("uid", specParams.uid);
    }
    if (specParams.tag) {
      params = params.append("tag", specParams.tag);
    }

    return this.http
      .get<Pagination>(this.baseUrl + "/Classes", {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          return response.body;
        })
      );
  }

  getDashboardCoursesByTutor(
    userId?: string
  ): AngularFirestoreCollection<Course> {
    const createdBy = this.userLogin ? this.userLogin.uid : userId;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", createdBy)
        .orderBy("updatedOn", "desc")
        .limit(3)
    );
  }

  //Tutor
  getCoursesByTutor(userId?: string): AngularFirestoreCollection<Course> {
    const createdBy = this.userLogin ? this.userLogin.uid : userId;

    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("createdBy", "==", createdBy)
        .orderBy("updatedOn", "desc")
    );
  }

  // get(id: string) {
  //   return this.coursesRef.doc(id).valueChanges(["modified"]);
  // }

  getCourseById(id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath).doc(id);

    return ref.get();
  }

  create(course: Course): any {
    course.status = 0;
    course.privacy = false;
    course.isActive = true;
    course.isDeleted = false;
    course.published = false;

    course.noStudent = 0;
    course.rating = 0;
    course.noRating = 0;
    course.noRating1 = 0;
    course.noRating2 = 0;
    course.noRating3 = 0;
    course.noRating4 = 0;
    course.noRating5 = 0;

    course.noQnA = 0;
    course.noLiked = 0;
    course.noComment = 0;

    //Moderator
    course.isReviewed = false;
    course.isApproved =
      this.userLogin.admin || this.userLogin.moderator || this.userLogin.tutor
        ? true
        : false;
    course.approvedOn = new Date();
    course.approvedBy = this.userLogin.uid;
    course.approvedAuthor = this.userLogin;

    course.createdOn = new Date();
    course.createdBy = this.userLogin.uid;
    course.updatedOn = new Date();
    course.updatedBy = this.userLogin.uid;
    course.authorName = this.userLogin.displayName;
    course.author = this.userLogin;
    course.timestamp = new Date().getTime();
    course.visibility = this.visibility;

    return this.coursesRef.add({ ...course }).then((docRef) => {
      return docRef.id;
    });
  }

  update(id: string, course: Course): Promise<void> {
    const dateNow = new Date();
    course.updatedBy = this.userLogin.uid;
    course.authorName = this.userLogin.displayName;
    const author = this.userLogin;

    const syncData = { id, ...course };
    this.syncCourse(syncData);

    return this.coursesRef.doc(id).update({
      title: course.title,
      description: course.description,
      purpose: course.purpose,
      tags: course.tags,
      coverUrl: course.coverUrl,
      authorName: course.authorName,
      author: author,
      privacy: course.privacy,
      status: course.status,
      updatedOn: dateNow,
      timestamp: new Date().getTime(),
      isReviewed: false,
    });
  }

  updateNoSectionLecture(
    id: string,
    noSection: number,
    noLecture: number
  ): Promise<void> {
    const dateNow = new Date();

    return this.coursesRef.doc(id).update({
      noSection: noSection,
      noLecture: noLecture,
      updatedOn: dateNow,
    });
  }

  updateNoExerciseQuiz(
    id: string,
    noExercise: number,
    noQuiz: number
  ): Promise<void> {
    const dateNow = new Date();

    return this.coursesRef.doc(id).update({
      noExercise: noExercise,
      noQuiz: noQuiz,
      updatedOn: dateNow,
    });
  }

  changeCover(id: string, coverUrl: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.coursesRef.doc(id).update({
      coverUrl: coverUrl,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      author: this.userLogin,
    });
  }

  isPublish(id: string, value: boolean): Promise<void> {
    const dateNow = new Date();
    const updatedBy = this.userLogin.uid;
    const authorName = this.userLogin.displayName;
    const author = this.userLogin;

    return this.coursesRef.doc(id).update({
      author: author,
      authorName: authorName,
      published: value,
      publishedOn: dateNow,
      updatedOn: dateNow,
      updatedBy: updatedBy,
    });
  }

  isActive(id: string, value: boolean): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.coursesRef
      .doc(id)
      .update({ isActive: value, updatedOn: updatedOn, updatedBy: updatedBy });
  }

  delete(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.coursesRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  syncCourse(course) {
    course.publishedOn ? delete course.publishedOn : null;
    course.createdOn ? delete course.createdOn : null;
    course.updatedOn ? delete course.updatedOn : null;
    course.tags = JSON.stringify(course.tags);
    course.fsId = course.id ? course.id : "";

    //const synObj = JSON.parse(JSON.stringify(course));

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/Classes", course).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
